import { PaginationSort } from '../gql/graphql';
import { getPaginatedNodes, toPagination } from './utils';
import { graphql } from '../gql';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

export const GET_BROKERAGE_ADDRESS = graphql(`
  query getBrokerageAddress(
    $pagination: PaginationRequest
    $filters: EntityFilterInput!
    $sort: PaginationSort
  ) {
    entities(pagination: $pagination, filters: $filters, sort: $sort) {
      edges {
        cursor
        node {
          uuid
          addresses(filters: { source: PRIMARY }) {
            edges {
              cursor
              node {
                uuid
                street1
                street2
                city
                type
                postalCode
                source
                regions {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      iso2Code
                    }
                  }
                }
                countries {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      iso2Code
                    }
                  }
                }
                primary
              }
            }
          }
        }
      }
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
        pageNumber
        pageSize
      }
    }
  }
`);

export function useGetBrokerageAddress({
  pageNumber = 0,
  brokerageUuid,
  enabled,
  sort,
}: {
  pageNumber?: number;
  brokerageUuid?: string;
  isParent?: boolean;
  enabled?: boolean;
  sort?: PaginationSort;
}) {
  const variables = {
    pagination: toPagination(pageNumber, 1),
    filters: {},
    sort,
  };
  variables.filters = {
    agency: true,
    entityUuid: brokerageUuid,
  };
  const { loading, error, data } = useQuery(GET_BROKERAGE_ADDRESS, {
    variables,
    skip: !enabled,
  });

  const brokerageAddress = useMemo(() => {
    if (!data || !data.entities || !data.entities.edges) return [];

    return getPaginatedNodes(data.entities).map((edge) => {
      const primaryAddress = edge.addresses.edges.find((item) => {
        const node = item.node;
        return node?.type?.includes('CORPORATE');
      });

      return {
        id: edge?.uuid,
        primaryAddressUuid: primaryAddress?.node.uuid,
        primaryStreet1: primaryAddress?.node.street1 ?? '',
        primaryStreet2: primaryAddress?.node.street2 ?? '',
        primaryCity: primaryAddress?.node.city ?? '',
        primaryStateName:
          primaryAddress?.node?.regions?.edges[0]?.node?.name ?? '',
        primaryStateUuid:
          primaryAddress?.node?.regions?.edges[0]?.node?.uuid ?? '',
        primaryPostalCode: primaryAddress?.node?.postalCode ?? '',
        primaryCountryUuid:
          primaryAddress?.node?.countries?.edges[0]?.node.uuid ?? '',
        primaryCountryName:
          primaryAddress?.node?.countries?.edges[0]?.node.name ?? '',
      };
    });
  }, [data]);

  return {
    error,
    loading,
    data,
    brokerageAddress,
  };
}
